import { Button, Stack, Typography } from '@mui/material';
import { SharedDialog } from 'customer-management/customer-details/view-contracts/dialogs';
import { DialogCommentField } from 'customer-management/customer-details/view-contracts/dialogs/dialog-comment-field';
import { useViewContractsState } from 'customer-management/customer-details/view-contracts/state/view-contracts-state';
import { useSnackbar } from 'notistack';
import { useCallback } from 'react';
import { useDeleteDraftContract, useSendContractNotification, useUpdateContract } from 'shared/mutations';
import { ContractDocument, I2PEnvironment, UserInfo } from 'shared/types';

interface ApproveDialogProps {
  contract: ContractDocument;
  user: UserInfo;
  environment: I2PEnvironment;
}
export function ApproveDialog({ contract, user, environment }: ApproveDialogProps) {
  const { enqueueSnackbar } = useSnackbar();
  const state = useViewContractsState();

  const updateContractMutation = useUpdateContract();
  const deleteDraftContractMutation = useDeleteDraftContract();
  const notificationMutation = useSendContractNotification();

  const onApprove = useCallback(async () => {
    const today = new Date();
    const comments = state.comments.value;
    contract.approvedBy = user.displayName;
    contract.approvedByEmail = user.email ?? user.displayName;
    contract.approvedDate = today.toISOString();
    contract.status = 'active';
    contract.approvalStatus = 'Approved';

    await updateContractMutation.mutateAsync(contract);
    await deleteDraftContractMutation.mutateAsync(contract.id);

    await notificationMutation.mutateAsync({
      contract,
      template: 'approve',
      comments,
      environment,
    });

    enqueueSnackbar('Contract Approved', { variant: 'success' });

    state.merge({
      approveDialog: false,
      comments: '',
    });
  }, [
    user,
    updateContractMutation,
    deleteDraftContractMutation,
    contract,
    state,
    notificationMutation,
    enqueueSnackbar,
    environment,
  ]);

  const loading =
    updateContractMutation.isLoading || deleteDraftContractMutation.isLoading || notificationMutation.isLoading;

  return (
    <SharedDialog
      open={state.approveDialog.get()}
      title='Approve Contract Changes'
      onCancel={() => {
        state.merge({
          approveDialog: false,
          comments: '',
        });
      }}
      submit={
        <Button onClick={onApprove} disabled={loading} variant='contained' color='secondary'>
          Approve
        </Button>
      }
    >
      <Stack spacing={1}>
        <Typography>You are about to Approve the changes to the contract.</Typography>
        <Typography>Please leave a comment on the reason for this approval.</Typography>
        <DialogCommentField />
      </Stack>
    </SharedDialog>
  );
}
